import React, { Component } from "react";
import { getBaseURL } from "components/helpers/HostURLHelper.jsx";
import { withStyles } from "@material-ui/core/styles";
import ComboBox from "./core/ComboBox.jsx";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from "recharts";
import qs from "query-string";
import Grid from "@material-ui/core/Grid";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Typography from "@material-ui/core/Typography";
import AnalystTestsLOV from "./common/AnalystTestsLOV.jsx";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Button from "@material-ui/core/Button";

const styles = (theme) => ({
  root: {
    padding: theme.spacing(2),
  },
  filterRow: {
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  card: {
    marginBottom: theme.spacing(2),
  },
});

const COLORS = [
  "#4caf50",
  "#f44336",
  "#9e9e9e",
  "#2196f3",
  "#ff9800",
  "#03a9f4",
  "#673ab7",
];

class UnifiedDashboard extends Component {
  state = {
    paper_id: "",
    allPapers: [],
    allBranches: [],
    allSections: [],
    selectedBranch: "",
    selectedSection: "",
    attendanceData: [],
    marksSummary: [],
    toppers: [],
    subjectAccuracy: [],
    marksDistribution: [],
    allSubjects: [],
    selectedSubject: "Overall",
    questionAnalysisData: {},
    activeQuestionSubject: "",
    isMobile: false,
    currentQuestionPage: 0,
    questionsPerPage: 10,
    test_info_id: "",
    program_id: "",
    test_type_id: "",
    allPrograms: [],
    allTestTypes: [],
  };

  componentDidMount() {
    this.fetchPrograms();
    this.fetchTestTypes();

    const isMobile = window.innerWidth <= 768;
    this.setState({ isMobile });
  }

  fetchPrograms = () => {
    $.get(getBaseURL() + "/global_programs").done((data) => {
      const allPrograms = [
        { name: "All", id: "" },
        ...JSON.parse(data)?.programs,
      ];
      this.setState({ allPrograms });
    });
  };

  fetchTestTypes = () => {
    $.get(getBaseURL() + "/get-test-types.json").done((data) => {
      const allTestTypes = [{ name: "All", id: "" }, ...data.test_types];
      this.setState({ allTestTypes });
    });
  };

  fetchFilters = () => {
    const { paper_id } = this.state;
    if (!paper_id) return;
    $.get(
      getBaseURL() + "/analysis/branches-sections.json?paper_id=" + paper_id
    ).done((data) => {
      const allBranches = [
        { name: "All", branch_id: "" },
        ...data.branches.map((b) => ({ name: b.name, branch_id: b.id })),
      ];
      const allSections = [
        { name: "All", section_id: "" },
        ...data.sections.map((s) => ({ name: s.name, section_id: s.id })),
      ];
      this.setState({ allBranches, allSections });
    });
  };

  fetchDashboardData = () => {
    this.fetchAttendance();
    this.fetchSubjectAccuracy();
    this.fetchTopBranchScorers();

    this.loadMarksReportData((data) => {
      this.fetchMarksSummary(data);

      this.fetchMarksDistribution(data);
    });

    this.setState({ currentQuestionPage: 0 });
  };

  loadMarksReportData = (callback) => {
    const { paper_id, selectedBranch, selectedSection } = this.state;
    const params = new URLSearchParams({
      paper_id,
      branch_id: selectedBranch,
      section_id: selectedSection,
    });

    $.get(
      getBaseURL() + "/analysis/test/marks-report.json?" + params.toString()
    ).done((data) => {
      this.setState({ marksReportData: data }, () => {
        if (callback) callback(data);
      });
    });
  };

  fetchAttendance = () => {
    const { paper_id, selectedBranch, selectedSection } = this.state;
    const params = new URLSearchParams({
      paper_id,
      branch_id: selectedBranch,
      section_id: selectedSection,
    });
    $.get(
      getBaseURL() +
        "/analysis/test/branches-attendance.json?" +
        params.toString()
    ).done((data) => {
      const attendanceData = data.attendance_data.map((b) => ({
        name: b.name,
        Present: b.branch_student_attendance,
        Absent: b.branch_student_count - b.branch_student_attendance,
      }));
      this.setState({ attendanceData });
    });
  };

  fetchMarksSummary = (data) => {
    const { paper_id, selectedBranch, selectedSection } = this.state;
    const params = new URLSearchParams({
      paper_id,
      branch_id: selectedBranch,
      section_id: selectedSection,
    });

    const schedule_data = data["schedule_data"];
    const testAverageData = {
      full_marks: schedule_data.total_marks * schedule_data.attended_students,
      attended_students: schedule_data.attended_students,
      total_awarded_marks: 0,
      subjects_info: [],
    };

    const subArray = [];
    if (schedule_data && schedule_data["subjects_info"]) {
      for (let sub_info of schedule_data["subjects_info"]) {
        subArray.push(sub_info.sub_short_name);
        testAverageData.subjects_info.push({
          sub_short_name: sub_info.sub_short_name,
          full_marks:
            sub_info.sub_total_marks * schedule_data.attended_students,
          sub_total_marks: sub_info.sub_total_marks,
          total_awarded_marks: 0,
        });
      }
    }

    const user_results = data["user_results"] || [];
    for (let user_result of user_results) {
      if (user_result["subject_results"]) {
        let subIndx = 0;
        for (let sub_result of user_result["subject_results"]) {
          if (
            sub_result.total_marks &&
            subArray[subIndx] === sub_result.sub_short_name
          ) {
            testAverageData.subjects_info[subIndx].total_awarded_marks +=
              sub_result.total_marks;
          }
          subIndx++;
        }
      }
      testAverageData.total_awarded_marks += user_result.total_marks || 0;
    }

    const avgRows = testAverageData.subjects_info.map((sub) => {
      const avg = (
        sub.total_awarded_marks / testAverageData.attended_students
      ).toFixed(2);
      const perc = ((100 * sub.total_awarded_marks) / sub.full_marks).toFixed(
        2
      );
      return {
        subject: sub.sub_short_name,
        average: parseFloat(avg),
      };
    });

    const overallAvg = (
      testAverageData.total_awarded_marks / testAverageData.attended_students
    ).toFixed(2);
    avgRows.push({ subject: "Overall", average: parseFloat(overallAvg) });
    const allSubjects = avgRows.map((r) => ({ subject: r.subject }));

    this.setState({ marksSummary: avgRows, allSubjects });
  };

  fetchTopBranchScorers = () => {
    const { paper_id, selectedBranch, selectedSection, selectedSubject } =
      this.state;
    const params = new URLSearchParams({
      paper_id,
      top_students: 100,
      branch_id: selectedBranch,
      section_id: selectedSection,
    }); // get more and then filter top 10 in frontend

    $.get(
      getBaseURL() + "/analysis/test/marks-report.json?" + params.toString()
    ).done((data) => {
      const user_results = data.user_results || [];

      const scoredUsers = user_results.map((u) => {
        let marks = 0;

        if (selectedSubject === "Overall") {
          marks = u.total_marks;
        } else if (u.subject_results) {
          const subResult = u.subject_results.find(
            (sr) => sr.sub_short_name === selectedSubject
          );
          marks = subResult?.total_marks || 0;
        }

        return {
          name: u.name,
          marks: marks,
          branch: u.branch_name,
        };
      });

      const topUsers = scoredUsers
        .filter((u) => u.marks != null)
        .sort((a, b) => b.marks - a.marks)
        .slice(0, 10);

      this.setState({ toppers: topUsers });
    });
  };

  fetchSubjectAccuracy = () => {
    const { paper_id, selectedBranch, selectedSection } = this.state;
    const params = new URLSearchParams({
      paper_id,
      top_students: 10,
      branch_id: selectedBranch,
      section_id: selectedSection,
    });

    $.get(
      getBaseURL() +
        "/analysis/test/question-analysis.json?" +
        params.toString()
    ).done((data) => {
      const questionAnalysisData = data.question_analysis;
      const subjects = Object.keys(questionAnalysisData);

      const subjectAccuracy = subjects.map((sub) => {
        let correct = 0,
          incorrect = 0,
          unattempted = 0;
        questionAnalysisData[sub].forEach((q) => {
          correct += q.correct;
          incorrect += q.incorrect;
          unattempted += q.unattempted;
        });
        return { subject: sub, correct, incorrect, unattempted };
      });

      const test_info_id = data?.schedule_data?.test_info_id;
      this.setState({
        test_info_id,
        subjectAccuracy,
        questionAnalysisData,
        activeQuestionSubject: subjects[0] || "PHYSICS", // first subject or fallback
      });
    });
  };

  fetchMarksDistribution = (data) => {
    const { selectedSubject } = this.state;

    const distMap = {};

    data.user_results.forEach((user) => {
      let marks = 0;

      if (selectedSubject === "Overall") {
        marks = user.total_marks;
      } else if (user.subject_results) {
        const subResult = user.subject_results.find(
          (sr) => sr.sub_short_name === selectedSubject
        );
        marks = subResult?.total_marks || 0;
      }

      const bucket = Math.floor(marks / 10) * 10;
      distMap[bucket] = (distMap[bucket] || 0) + 1;
    });

    const marksDistribution = Object.entries(distMap)
      .map(([k, v]) => ({ marks: Number(k), count: v }))
      .sort((a, b) => a.marks - b.marks);

    this.setState({ marksDistribution });
  };

  handleBranchChange = (e, option) => {
    this.setState(
      { selectedBranch: option?.branch_id || "" },
      this.fetchDashboardData
    );
  };

  handleSectionChange = (e, option) => {
    this.setState(
      { selectedSection: option?.section_id || "" },
      this.fetchDashboardData
    );
  };

  handlePaperChange = (option) => {
    this.setState({ paper_id: option?.paper_id || "" }, () => {
      this.fetchFilters();
      this.fetchDashboardData();
    });
  };

  handleSubjectChange = (e, option) => {
    const selectedSubject = option?.subject || "Overall";
    this.setState({ selectedSubject, currentQuestionPage: 0 }, () => {
      const { marksReportData } = this.state;
      if (marksReportData) {
        this.fetchMarksDistribution(marksReportData);
      }
      this.fetchTopBranchScorers(); // fetch fresh subject-wise data
    });
  };

  processQuestionWiseAnalysis = () => {
    const {
      activeQuestionSubject,
      questionAnalysisData,
      currentQuestionPage,
      questionsPerPage,
      isMobile,
    } = this.state;

    const data = questionAnalysisData?.[activeQuestionSubject] || [];

    const paged = isMobile
      ? data.slice(
          currentQuestionPage * questionsPerPage,
          (currentQuestionPage + 1) * questionsPerPage
        )
      : data;

    return paged.map((q) => ({
      question_no: `Q${q.question_no}`,
      Correct: q.correct,
      Incorrect: q.incorrect,
      Unattempted: q.unattempted,
    }));
  };

  isMobile = () => window.innerWidth <= 768;

  renderPieChart = (dataKey, title) => {
    const data = this.state.subjectAccuracy.map((d) => ({
      name: d.subject,
      value: d[dataKey],
    }));
    return (
      <Card style={{ marginBottom: 16 }}>
        <CardContent>
          <Typography variant="h6" style={{ textTransform: "none" }}>
            {title}
          </Typography>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={80}
                label
              >
                {data.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    );
  };

  render() {
    const { classes } = this.props;
    const {
      paper_id,
      allPapers,
      allBranches,
      allSections,
      attendanceData,
      marksSummary,
      toppers,
      marksDistribution,
      allSubjects,
    } = this.state;

    return (
      <div className={classes.root}>
        <Grid container spacing={2} className={classes.filterRow}>
          <Grid item xs={6} sm={3}>
            <ComboBox
              options={this.state.allPrograms}
              label="Program"
              onChange={(e, opt) =>
                this.setState({
                  program_id: opt?.id || "",
                  paper_id: "",
                  allPapers: [],
                })
              }
              optionLabel={(opt) =>
                opt?.short_name ? opt?.short_name : opt?.name
              }
            />
          </Grid>

          <Grid item xs={6} sm={3}>
            <ComboBox
              options={this.state.allTestTypes}
              label="Test Type"
              onChange={(e, opt) =>
                this.setState({
                  test_type_id: opt?.id || "",
                  paper_id: "",
                  allPapers: [],
                })
              }
              optionLabel={(opt) =>
                opt?.short_name ? opt?.short_name : opt?.name
              }
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <AnalystTestsLOV
              onSelectSchedule={this.handlePaperChange}
              program_id={this.state.program_id}
              test_type_id={this.state.test_type_id}
              {...this.props}
            />
            {/* <ComboBox options={allPapers} onChange={this.handlePaperChange} label="Select Test" optionLabel={(opt) => opt.name} /> */}
          </Grid>
          <Grid item xs={6} sm={3}>
            <ComboBox
              options={allBranches}
              onChange={this.handleBranchChange}
              label="Filter by Branch"
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <ComboBox
              options={allSections}
              onChange={this.handleSectionChange}
              label="Filter by Section"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <ComboBox
              options={allSubjects}
              onChange={this.handleSubjectChange}
              label="Filter by Subject"
              optionLabel={(opt) => opt.subject}
            />
          </Grid>
        </Grid>

        {!paper_id && (
          <Typography
            variant="body1"
            style={{ textAlign: "center", padding: 20 }}
          >
            Please select a test to view the dashboard.
          </Typography>
        )}

        {paper_id && (
          <React.Fragment>
            {/* Attendance Summary */}
            <Card className={classes.card}>
              <CardContent>
                <Typography variant="h6" style={{ textTransform: "none" }}>
                  Attendance Summary
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={attendanceData}
                    margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
                    barCategoryGap={10}
                    barGap={4}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar
                      dataKey="Present"
                      stackId="a"
                      fill="#4caf50"
                      barSize={24}
                    />
                    <Bar
                      dataKey="Absent"
                      stackId="a"
                      fill="#f44336"
                      barSize={24}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Marks Distribution Curve */}
            <Card className={classes.card}>
              <CardContent>
                <Typography variant="h6" style={{ textTransform: "none" }}>
                  Marks Distribution
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart
                    data={marksDistribution}
                    margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="marks"
                      label={{
                        value: "Marks",
                        position: "insideBottomRight",
                        offset: -5,
                      }}
                    />
                    <YAxis
                      label={{
                        value: "Students",
                        angle: -90,
                        position: "insideLeft",
                      }}
                    />
                    <Tooltip />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="count"
                      stroke="#8884d8"
                      strokeWidth={2}
                      dot={true}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Average Marks */}
            <Card className={classes.card}>
              <CardContent>
                <Typography variant="h6" style={{ textTransform: "none" }}>
                  Average Marks
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={marksSummary}
                    margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
                    barCategoryGap={15}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="subject" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="average" fill="#2196f3" barSize={24} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Top 10 Scorers by Branch */}
            <Card className={classes.card}>
              <CardContent>
                <Typography variant="h6" style={{ textTransform: "none" }}>
                  Top 10 Students
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={toppers}
                    layout="vertical"
                    margin={{ top: 10, right: 20, left: 20, bottom: 10 }}
                    barGap={4}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="marks" fill="#ff9800" barSize={18} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card className={classes.card}>
              <CardContent>
                <Typography
                  variant="h6"
                  style={{ textTransform: "none", marginBottom: 16 }}
                >
                  Question-wise Analysis
                </Typography>

                <Button
                  variant="contained"
                  color="primary"
                  disabled={!this.state.test_info_id}
                  onClick={() => {
                    const testViewUrl = `/analysis/test/questions/view?test_id=${this.state.test_info_id}&mode=view`;
                    window.open(testViewUrl, "_blank");
                  }}
                  style={{ marginBottom: 16, marginLeft: 2 }}
                >
                  View All Questions
                </Button>

                <Tabs
                  value={this.state.activeQuestionSubject}
                  onChange={(e, newValue) =>
                    this.setState({
                      activeQuestionSubject: newValue,
                      currentQuestionPage: 0,
                    })
                  }
                  indicatorColor="primary"
                  textColor="primary"
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  {Object.keys(this.state.questionAnalysisData).map(
                    (subject) => (
                      <Tab key={subject} label={subject} value={subject} />
                    )
                  )}
                </Tabs>

                <div
                  style={{
                    paddingLeft: this.isMobile() ? 0 : 16,
                    paddingRight: this.isMobile() ? 0 : 16,
                  }}
                >
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart
                      data={this.processQuestionWiseAnalysis()}
                      margin={{ top: 10, right: 20, left: 0, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="question_no"
                        angle={-45}
                        textAnchor="end"
                        interval={0}
                        height={this.isMobile() ? 60 : 80}
                      />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="Correct" stackId="a" fill="#4caf50" />
                      <Bar dataKey="Incorrect" stackId="a" fill="#f44336" />
                      <Bar dataKey="Unattempted" stackId="a" fill="#9e9e9e" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                {this.state.isMobile && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      marginTop: 10,
                    }}
                  >
                    <button
                      disabled={this.state.currentQuestionPage === 0}
                      onClick={() =>
                        this.setState({
                          currentQuestionPage:
                            this.state.currentQuestionPage - 1,
                        })
                      }
                    >
                      Previous
                    </button>
                    {(() => {
                      const {
                        currentQuestionPage,
                        questionsPerPage,
                        activeQuestionSubject,
                        questionAnalysisData,
                      } = this.state;
                      const data =
                        questionAnalysisData?.[activeQuestionSubject] || [];

                      const pagedData = data.slice(
                        currentQuestionPage * questionsPerPage,
                        (currentQuestionPage + 1) * questionsPerPage
                      );

                      const firstQ = pagedData[0]?.question_no;
                      const lastQ =
                        pagedData[pagedData.length - 1]?.question_no;

                      return (
                        <span style={{ margin: "0 10px" }}>
                          {activeQuestionSubject?.substring(0, 3).toUpperCase()}{" "}
                          Q{firstQ}–Q{lastQ}
                        </span>
                      );
                    })()}
                    <button
                      disabled={
                        (this.state.questionAnalysisData?.[
                          this.state.activeQuestionSubject
                        ]?.length || 0) <=
                        (this.state.currentQuestionPage + 1) *
                          this.state.questionsPerPage
                      }
                      onClick={() =>
                        this.setState({
                          currentQuestionPage:
                            this.state.currentQuestionPage + 1,
                        })
                      }
                    >
                      Next
                    </button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Subject-wise Accuracy Pie Charts */}
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                {this.renderPieChart("correct", "Correct Questions")}
              </Grid>
              <Grid item xs={12} md={4}>
                {this.renderPieChart("incorrect", "Incorrect Questions")}
              </Grid>
              <Grid item xs={12} md={4}>
                {this.renderPieChart("unattempted", "Unattempted Questions")}
              </Grid>
            </Grid>
          </React.Fragment>
        )}
      </div>
    );
  }
}

export default withStyles(styles)(UnifiedDashboard);
